/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { BigNeutral } from '../../forms/elements/buttons';
import { ColumnConfig } from '../../layout/table/columnConfig';
import Table from '../../layout/table/Table';
import { sortByDateProp, sortByProp } from '../../language/module';
import { Column } from '../../layout/flexbox';
import { COLUMN_SPACING } from '../../forms/styles';
import LabeledData from '../../forms/elements/LabeledData';
import { EntitlementHotlist } from '../entitlements/models';
import { getResource } from '../../language/resources/getResource';
import { formatDate, toDateOnly } from '../../language/time';

const styles = {
  tableData: css`
    vertical-align: center !important;
  `,

  removeButton: css`
    padding: 5px 10px;
  `,
};


/**
 * HotlistTable: a component that renders a list of hotlists in table form including the name of the hotlist
 * (indicating whether it is global or specific to a resort), the expiration of the hotlist entry,
 * and a button to remove the hotlist entry. When the sign button is clicked, calls `props.onRemoveClick`.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class HotlistTable extends React.PureComponent<{
  guestName: string;
  isLoading: boolean;
  data: EntitlementHotlist[];
  dateFormat: string;
  error?: string | undefined;
  onRemoveClick: (hotlist: EntitlementHotlist) => void;
  shouldAllowRemove: (hotlist: EntitlementHotlist) => boolean;
  rootCss?: SerializedStyles;
  resources?: Record<string, any>;
}> {
    
  private makeClickToRemoveHandler = (hotlist: EntitlementHotlist) => () => this.props.onRemoveClick(hotlist);

  private columnConfig: Array<ColumnConfig<EntitlementHotlist>> = [
    {
      id: 1, 
      title:  getResource(this.props.resources, 'HOTLIST_TABLE_HOTLIST'),
      onSort: sortByProp('title', false),
    },
    {
      id: 2, 
      title:  getResource(this.props.resources, 'HOTLIST_TABLE_DURATION'),
      onSort: sortByDateProp('expirationDate'),
      defaultSortingColumn:    true,
    },
    {
      id: 3, 
      title:  getResource(this.props.resources, 'HOTLIST_TABLE_COMMENT'),
      onSort: sortByProp('hotlistComment', false),
    },
    {
      id: 4, 
      title: getResource(this.props.resources, 'HOTLIST_TABLE_REMOVE'),
    },
  ];

  public renderRow = (hotlist: EntitlementHotlist): React.ReactNode => {

    const effectiveDate = formatDate(toDateOnly(hotlist.effectiveDate!)!, this.props.dateFormat);
    const expirationDate = formatDate(toDateOnly(hotlist.expirationDate!)!, this.props.dateFormat);

    const dateString = effectiveDate ? `${effectiveDate} - ${expirationDate}` :
      expirationDate ? getResource(this.props.resources, 'HOTLIST_TABLE_UNTIL_EXPIRATION', expirationDate) :
      getResource(this.props.resources, 'HOTLIST_TABLE_INDEFINITE');

    return <tr key={hotlist.entitlementHotlistId}>
      <td css={styles.tableData}>{hotlist.title}</td>
      <td css={css`
          width: 225px;
          text-align: center;
          ${styles.tableData};
        `}>
        {dateString}
      </td>
      <td css={css`
          max-width: 375px;
          overflow-wrap: break-word;
          ${styles.tableData};
        `}>
        {hotlist.hotlistComment}
      </td>
      <td css={styles.tableData}>
        { this.props.shouldAllowRemove(hotlist) && (<BigNeutral onClick={this.makeClickToRemoveHandler(hotlist)}
          css={styles.removeButton}>
            {getResource(this.props.resources, 'HOTLIST_TABLE_REMOVE')}
          </BigNeutral>)}
      </td>
    </tr>;
  };


  public render = () => (
    <Column rootCss={css`
              ${COLUMN_SPACING};
              ${this.props.rootCss};
            `}>
      <LabeledData label={getResource(this.props.resources, 'HOTLIST_TABLE_GUEST_LABEL')} data={this.props.guestName}/>

      <Table columns={this.columnConfig}
             data={this.props.data}
             error={this.props.error}
             isLoading={this.props.isLoading}
             noDataMessage={getResource(this.props.resources, 'HOTLIST_TABLE_NO_HOTLIST')}
             renderRow={this.renderRow}/>
    </Column>
  );
}
